import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../adminLayout';
import { IntlMessages } from '../../../helper/langHelper';
import { getClusters } from '../../../services/admin/clusters';
import { UsersContainer, UsersHeader, LoaderWrapper, IconButton } from '../users/Users.styles';
import { Table } from '../../../components/uielements/table';
import { PrimaryButton } from '../users/modals/UserPromoteModal.styles';
import CreateAndUpdateClusterModal from './modals/CreateAndUpdateClusterModal';
import ClusterFilter from './ClusterFilter';
import { ButtonWrapper } from './Clusters.styles';

const Clusters = () => {
  const [page, setPage] = useState(0);
  const [perPage] = useState(10);
  const [editClusterData, setEditClusterData] = useState(null);
  const [addClusterData, setAddClusterData] = useState(false);

  const [filters, setFilters] = useState({
    regionId: '',
    precinctId: '',
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['adminClusters', page, perPage, filters],
    queryFn: () =>
      getClusters(page, perPage, {
        regionId: filters.regionId,
        precinctId: filters.precinctId,
      }),
  });

  const navigate = useNavigate();

  const tableData = data?.data ?? [];
  const total = data?.total ?? 0;

  const columns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.name" defaultMessage="Cluster Name" />,
        accessor: 'name',
        width: '30%',
      },
      {
        Header: <IntlMessages id="table.col.description" defaultMessage="Description" />,
        accessor: 'description',
        width: '45%',
      },
      {
        Header: <IntlMessages id="table.col.actions" defaultMessage="Actions" />,
        accessor: 'actions',
        isAction: true,
        width: '25%',
      },
    ],
    []
  );

  const actionList = [
    (data) => (
      <IconButton onClick={() => setEditClusterData(data)} variant="promote">
        <span style={{ fontSize: '13px', padding: '0 6px' }}>Edit</span>
      </IconButton>
    ),
    (data) => (
      <IconButton
        onClick={() => navigate(`/admin/cluster/manageQuestions/${data._id}/${data.questionCode}`)}
        variant="promote"
      >
        <span style={{ fontSize: '13px', padding: '0 6px' }}>Add Qns</span>
      </IconButton>
    ),
    (data) => (
      <IconButton
        onClick={() =>
          navigate('/admin/clusterFlow/test', {
            state: {
              clusterArray: [data].map((item) => ({
                _id: item._id,
                name: item.name,
                questionCode: item.questionCode,
                id: item.id,
              })),
            },
          })
        }
        variant="test"
      >
        {console.log(data)}
        <span style={{ fontSize: '13px', padding: '0 6px' }}>Test Flow</span>
      </IconButton>
    ),
  ];

  if (isLoading) {
    return (
      <AdminLayout pageTitle="Clusters" activeTab="clusters">
        <LoaderWrapper>Loading...</LoaderWrapper>
      </AdminLayout>
    );
  }

  if (isError) {
    return (
      <AdminLayout pageTitle="Clusters" activeTab="clusters">
        <LoaderWrapper>Oops! Error Encountered</LoaderWrapper>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="clusters">
      <UsersContainer>
        <UsersHeader>
          <IntlMessages id="nav.clusters" defaultMessage="Clusters" />

          <ButtonWrapper>
            <ClusterFilter
              filters={filters}
              onApply={(selectedFilters) => {
                setPage(0);
                setFilters(selectedFilters);
              }}
            />
            <PrimaryButton onClick={() => setAddClusterData(true)}>Add Cluster</PrimaryButton>
          </ButtonWrapper>
        </UsersHeader>

        <Table
          data={tableData}
          columns={columns}
          hasFooter={true}
          actions={actionList}
          page={page}
          pageSize={perPage}
          prevPage={() => setPage((p) => Math.max(0, p - 1))}
          nextPage={() => setPage((p) => p + 1)}
          total={total}
        />

        {editClusterData && (
          <CreateAndUpdateClusterModal
            onClose={() => setEditClusterData(null)}
            isEdit={true}
            clusterId={editClusterData._id}
            initialData={{
              name: editClusterData.name,
              description: editClusterData.description,
            }}
          />
        )}
        {addClusterData && (
          <CreateAndUpdateClusterModal
            onClose={() => setAddClusterData(false)}
            isEdit={false}
            clusterId={null}
          />
        )}
      </UsersContainer>
    </AdminLayout>
  );
};

export default Clusters;
